import React from "react";
import { useTranslation } from "react-i18next";
import SkeletonLoader from "@/components/ui/SkeletonLoader";
import {
  toNumberOrNull,
  formatChangePct as formatChange,
  getChangeColor,
} from "./chartHelpers";

const ScoreCard = ({
  title,
  value,
  icon,
  isLoaded = false,
  error = null,
  size = "md",
  change = null,
  iconColor = "var(--brand-color-1)",
  iconBgColor = "var(--bg-surface)",
  cardBgColor = "var(--bg-elevated)",
  titleColor = "var(--text-secondary)",
  valueColor = "var(--text-primary)",
  className = "",
  tooltipText = null,
}) => {
  const { t } = useTranslation();
  // Size configurations
  const sizeConfig = {
    sm: {
      cardPadding: "p-2",
      iconSize: "20px",
      titleSize: "fs-6",
      valueSize: "h5",
      changeSize: "small",
      minHeight: "80px",
    },
    md: {
      cardPadding: "p-3",
      iconSize: "24px",
      titleSize: "fs-6",
      valueSize: "h4",
      changeSize: "small",
      minHeight: "120px",
    },
    lg: {
      cardPadding: "p-3",
      iconSize: "28px",
      titleSize: "fs-5",
      valueSize: "h3",
      changeSize: "fs-6",
      minHeight: "120px",
    },
  };

  const config = sizeConfig[size] || sizeConfig.md;

  if (error) {
    return (
      <div
        className={`card ${config.cardPadding} ${className} card-radius d-flex align-items-center justify-content-center`}
        style={{
          backgroundColor: cardBgColor,
          boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
          borderColor: "var(--border)",
          minHeight: config.minHeight,
        }}
      >
        <div className="w-100 text-center">
          <div
            className="alert alert-danger mb-0 text-break"
            role="alert"
            style={{
              fontSize: "0.875rem",
              lineHeight: "1.4",
              padding: "0.5rem 0.75rem",
            }}
          >
            <i className="bi bi-exclamation-triangle me-2"></i>
            <span className="text-wrap">
              {t("common.genericError", "An error occurred. Please try again.")}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // Loading state
  if (!isLoaded) {
    return (
      <div
        className={`card ${config.cardPadding} ${className} card-radius`}
        style={{
          backgroundColor: cardBgColor,
          boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
          borderColor: "var(--border)",
          minHeight: config.minHeight,
        }}
      >
        <div className="d-flex align-items-center mb-3">
          <div
            className="d-flex align-items-center justify-content-center me-3"
            style={{
              width: "40px",
              height: "40px",
              backgroundColor: iconBgColor,
              minWidth: "40px",
              borderRadius: "30%",
            }}
            aria-hidden="true"
          >
            {typeof icon === "string" ? (
              <i
                className={icon}
                style={{
                  color: iconColor,
                  fontSize: config.iconSize,
                }}
              />
            ) : (
              <span style={{ color: iconColor, fontSize: config.iconSize }}>
                {icon}
              </span>
            )}
          </div>
          <span
            className={`fw-normal ${config.titleSize} text-break`}
            style={{ color: titleColor }}
          >
            {title}
            {tooltipText && (
              <i
                className="bi bi-info-circle ms-1"
                title={tooltipText}
                style={{
                  cursor: "pointer",
                  fontSize: "0.85em",
                  color: "var(--text-secondary)",
                }}
              ></i>
            )}
          </span>
        </div>
        <div className="d-flex align-items-end" style={{ marginLeft: "8px" }}>
          <SkeletonLoader width="42%" height="28px" className="me-2" />
          <SkeletonLoader width="26%" height="16px" />
        </div>
      </div>
    );
  }

  const numChange = toNumberOrNull(change);
  const isValidChange = numChange !== null;

  return (
    <div
      className={`card ${config.cardPadding} ${className} card-radius`}
      style={{
        backgroundColor: cardBgColor,
        boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
        borderColor: "var(--border)",
        minHeight: config.minHeight,
      }}
    >
      <div className="d-flex align-items-center mb-3">
        <div
          className="d-flex align-items-center justify-content-center me-3"
          style={{
            width: "40px",
            height: "40px",
            backgroundColor: iconBgColor,
            minWidth: "40px",
            borderRadius: "30%",
          }}
          aria-hidden="true"
        >
          {typeof icon === "string" ? (
            <i
              className={icon}
              style={{
                color: iconColor,
                fontSize: config.iconSize,
              }}
            />
          ) : (
            <span style={{ color: iconColor, fontSize: config.iconSize }}>
              {icon}
            </span>
          )}
        </div>
        <span
          className={`fw-normal ${config.titleSize} text-break`}
          style={{ color: titleColor }}
        >
          {title}
          {tooltipText && (
            <i
              className="bi bi-info-circle ms-1"
              title={tooltipText}
              style={{
                cursor: "pointer",
                fontSize: "0.85em",
                color: "var(--text-secondary)",
              }}
            ></i>
          )}
        </span>
      </div>
      <div className="d-flex align-items-end" style={{ marginLeft: "8px" }}>
        <div
          className={`fw-bold ${config.valueSize} mb-0 me-2 text-break`}
          style={{
            color: valueColor,
            lineHeight: "1.2",
          }}
        >
          {value}
        </div>
        {isValidChange && (
          <span
            className={`${config.changeSize} fw-medium d-flex align-items-center text-nowrap`}
            style={{
              color: getChangeColor(change),
              lineHeight: "1.2",
              marginBottom: "2px",
            }}
          >
            <i
              className={`me-1 ${
                numChange >= 0
                  ? "bi bi-arrow-up-right"
                  : "bi bi-arrow-down-right"
              }`}
            ></i>
            {formatChange(change)}
          </span>
        )}
      </div>
    </div>
  );
};

export default ScoreCard;
